import React, { useState } from 'react';
import { Minus, Plus } from 'lucide-react';

const NewSkillFocus = ({ newSkill, handleUpdate, handleExit }) => {
  const [skillProgress, setSkillProgress] = useState(newSkill);

  const getProgressConfig = () => {
    const totalProgress = Object.values(skillProgress).reduce((sum, value) => {
      return sum + (typeof value === 'number' ? value : 0);
    }, 0) - (skillProgress.skill_id || 0); // Exclude skill_id from total

    if (totalProgress === 0) {
      return {
        bg: 'bg-gradient-to-r from-red-500 to-pink-500',
        label: 'Starting',
        emoji: '🆕',
        description: 'Ready to begin training'
      };
    } else if (totalProgress < 10) {
      return {
        bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
        label: 'Learning',
        emoji: '🎯',
        description: 'Building fundamentals'
      };
    } else if (totalProgress < 25) {
      return {
        bg: 'bg-gradient-to-r from-blue-500 to-purple-500',
        label: 'Developing',
        emoji: '📈',
        description: 'Making solid progress'
      };
    } else {
      return {
        bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
        label: 'Advanced',
        emoji: '🏆',
        description: 'Competition ready'
      };
    }
  };

  const config = getProgressConfig();

  const skillStages = [
    { key: 'in_rig', label: 'In Rig', emoji: '🤸', desc: 'Safety harness practice' },
    { key: 'to_pit', label: 'To Pit', emoji: '🕳️', desc: 'Landing into foam pit' },
    { key: 'mat_with_catch', label: 'Caught', emoji: '🙌', desc: 'Landed with assistance' },
    { key: 'to_mat', label: 'To Mat', emoji: '🎪', desc: 'Landing on crash mat' },
    { key: 'to_bed', label: 'To Bed', emoji: '🔵', desc: 'Landing on trampoline bed' },
    { key: 'in_combination', label: 'In Combo', emoji: '🔗', desc: 'Part of skill sequence' },
    { key: 'in_routine', label: 'In Routine', emoji: '💃', desc: 'Part of full routine' },
    { key: 'competed', label: 'Competed', emoji: '🥇', desc: 'Performed in competition' }
  ];

  const updateProgress = (key, delta) => {
    setSkillProgress(prev => ({
      ...prev,
      [key]: Math.max(0, (prev[key] || 0) + delta)
    }));
  };

  const CounterRow = ({ stage }) => (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{stage.emoji}</span>
          <div>
            <div className="font-semibold text-white text-xl">{stage.label}</div>
            <div className="text-white/80 text-lg">{stage.desc}</div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => updateProgress(stage.key, -1)}
          className="w-10 h-10 rounded-full bg-red-500/80 hover:bg-red-500 text-white font-bold transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center shadow-lg"
        >
          <Minus className="h-4 w-4" />
        </button>
        
        <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 min-w-[60px] text-center">
          <span className="text-3xl font-bold text-white">{skillProgress[stage.key] || 0}</span>
        </div>
        
        <button
          onClick={() => updateProgress(stage.key, 1)}
          className="w-10 h-10 rounded-full bg-blue-500/80 hover:bg-blue-500 text-white font-bold transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center shadow-lg"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`${config.bg} p-6 text-center flex-shrink-0`}>
          <div className="text-4xl mb-2">{config.emoji}</div>
          <h2 className="text-3xl font-bold text-white drop-shadow-lg mb-1">
            {newSkill.name}
          </h2>
          <p className="p-style text-lg" style={{color: 'RGBA(255,255,255,0.9)'}}>
            {config.description}
          </p>
          <div className="mt-3 inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-white/90 text-lg font-medium">{config.label}</span>
          </div>
        </div>

        {/* Progress Stages */}
        <div className={`${config.bg} flex-1 overflow-y-auto`}>
          <div className="p-6 space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">
              Training Progress 📊
            </h3>
            
            {skillStages.map((stage) => (
              <CounterRow key={stage.key} stage={stage} />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white p-6 flex gap-3 flex-shrink-0 text-lg font-semibold">
          <button
            onClick={() => handleUpdate(skillProgress)}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
          >
            Save 💾
          </button>
          
          <button
            onClick={handleExit}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
          >
            Cancel ❌
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewSkillFocus;